"use client";

import React, { useEffect, useRef } from "react";
import { useForm } from "react-hook-form";
import { Form, FormField, FormControl } from "@drpcrd/shared/ui";
import { ArrowUp, Paperclip, X } from "lucide-react";
import {
  BRIEF_ATTACHMENT_ACCEPT,
  cn,
  toast,
  type BriefAttachmentContentType,
  type ChatAttachment,
} from "@drpcrd/utils";
import { CustomSpinner } from "../icons/custom-spinner";
import { CHAT_ATTACHMENT_CHIP_CLASSNAME } from "./chat-attachment-link";
import {
  useChatAttachmentUpload,
  type ChatAttachmentUploadTarget,
} from "./use-chat-attachment-upload";

type ActivityChatInputData = {
  message: string;
};

type ActivityChatInputProps = {
  /** Resolve `false` to keep the draft (text and attachment) for a retry. */
  onSend?: (
    message: string,
    attachment?: ChatAttachment,
  ) => void | boolean | Promise<void | boolean>;
  /** Issues a signed PUT URL so the file can upload before send. */
  requestUploadTarget: (
    fileType: BriefAttachmentContentType,
  ) => Promise<ChatAttachmentUploadTarget>;
  /** Deletes a draft object from GCS when the sender removes/replaces it. */
  deleteUploadedAttachment?: (key: string) => Promise<void>;
  placeholder?: string;
  defaultMessage?: string;
  disabled?: boolean;
};

export const ActivityChatInput = ({
  onSend,
  requestUploadTarget,
  deleteUploadedAttachment,
  placeholder = "Type here",
  defaultMessage = "",
  disabled,
}: ActivityChatInputProps) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement | null>(null);
  const form = useForm<ActivityChatInputData>({
    defaultValues: { message: defaultMessage },
    mode: "onSubmit",
  });

  const {
    handleSubmit,
    formState: { isSubmitting },
    control,
    watch,
    reset,
  } = form;

  const messageValue = watch("message");

  // One line when empty, growing with the text up to the textarea's max
  // height and scrolling beyond it, so a paragraph isn't squeezed into a
  // single line.
  useEffect(() => {
    const textarea = textareaRef.current;
    if (!textarea) {
      return;
    }
    textarea.style.height = "auto";
    textarea.style.height = `${textarea.scrollHeight}px`;
  }, [messageValue]);

  const {
    pendingName,
    attachment,
    isUploading,
    clearAttachment,
    removeAttachment,
    onFileSelected,
  } = useChatAttachmentUpload({
    requestUploadTarget,
    deleteUploadedAttachment,
    isSending: isSubmitting,
  });

  const hasAttachmentDraft = isUploading || attachment != null;
  const canSend =
    !isUploading &&
    !isSubmitting &&
    !disabled &&
    (Boolean(messageValue?.trim()) || attachment != null);

  const onSubmit = async (data: ActivityChatInputData) => {
    const text = data.message.trim();
    if (!text && !attachment) {
      return;
    }
    if (isUploading) {
      return;
    }

    // The draft stays (with the send spinner) until the send resolves, and is
    // kept for a retry if it fails.
    try {
      const sent = onSend ? await onSend(text, attachment ?? undefined) : true;
      if (sent === false) {
        return;
      }
      reset({ message: "" });
      clearAttachment();
    } catch (error) {
      toast("Failed to send message", {
        description: "Something went wrong. Please try again.",
      });
      console.error(error);
    }
  };

  // Uploading shows the picked file's name with a spinner; once ready, the
  // uploaded attachment's name with a paperclip.
  const draftName = isUploading ? pendingName : attachment?.name;

  // Same footprint as `ChatAttachmentLink` in the feed.
  const draftChip = draftName ? (
    <div
      className={cn(
        CHAT_ATTACHMENT_CHIP_CLASSNAME,
        "rounded-lg bg-primary-50 pr-2 text-black",
      )}
    >
      {isUploading ? (
        <CustomSpinner
          className="h-2 w-2 shrink-0 animate-spin text-black"
          aria-hidden
        />
      ) : (
        <Paperclip
          className="h-2 w-2 shrink-0 text-black"
          size={8}
          strokeWidth={2}
          aria-hidden
        />
      )}
      <span className="min-w-0 truncate">{draftName}</span>
      <button
        type="button"
        aria-label={isUploading ? "Cancel upload" : "Remove attachment"}
        className="shrink-0 text-black disabled:opacity-50"
        // Locked while sending: the file is already on its way with the
        // message, so removing it now would delete what the recipient opens.
        disabled={isSubmitting || disabled}
        onClick={removeAttachment}
      >
        <X className="h-2 w-2" />
      </button>
    </div>
  ) : null;

  return (
    <Form {...form}>
      <div className="mx-auto w-full">
        <div
          className={cn(
            "overflow-hidden rounded-3xl border border-neutral-500 bg-white px-2 py-1",
            hasAttachmentDraft ? "flex flex-col gap-0.5" : "flex items-center",
          )}
        >
          {draftChip}

          <div
            className={cn(
              "flex min-w-0 w-full",
              hasAttachmentDraft ? "items-end" : "items-center",
            )}
          >
            <FormField
              control={control}
              name="message"
              render={({ field }) => (
                <FormControl>
                  <textarea
                    {...field}
                    ref={(element) => {
                      field.ref(element);
                      textareaRef.current = element;
                    }}
                    rows={1}
                    aria-label={placeholder}
                    placeholder={placeholder}
                    // Read-only rather than disabled while sending, so the
                    // cursor stays in the box for the next message.
                    readOnly={disabled || isSubmitting}
                    className="max-h-24 min-h-5 w-full min-w-0 resize-none overflow-y-auto border-none bg-transparent leading-5 text-gray-700 placeholder-gray-400 focus:outline-none"
                  />
                </FormControl>
              )}
            />
            <div className="mr-0.5 flex shrink-0 items-center gap-2 self-end">
              <button
                type="button"
                aria-label="Attach file"
                onClick={() => fileInputRef.current?.click()}
                className="flex h-4 w-4 items-center justify-center rounded-full border border-neutral-500 bg-white disabled:opacity-50"
                disabled={isSubmitting || disabled || isUploading}
              >
                <Paperclip
                  className="text-primary h-2 w-2"
                  size={8}
                  strokeWidth={2}
                />
              </button>
              <button
                type="button"
                aria-label={isSubmitting ? "Sending message" : "Send message"}
                aria-busy={isSubmitting}
                onClick={handleSubmit(onSubmit)}
                className={cn(
                  "bg-primary flex h-4 w-4 items-center justify-center rounded-full disabled:pointer-events-none",
                  // Stays purple with a spinner while sending; greys out only
                  // when there's nothing to send yet.
                  !isSubmitting &&
                    "disabled:border-neutral-500 disabled:bg-neutral-500 disabled:text-neutral-900 disabled:[&>svg]:text-neutral-900",
                )}
                disabled={!canSend}
              >
                {isSubmitting ? (
                  <CustomSpinner
                    className="h-2 w-2 shrink-0 animate-spin text-white"
                    aria-hidden
                  />
                ) : (
                  <ArrowUp className="h-2 w-2 text-white" />
                )}
              </button>
            </div>
          </div>
        </div>

        <input
          ref={fileInputRef}
          type="file"
          accept={BRIEF_ATTACHMENT_ACCEPT}
          className="hidden"
          disabled={disabled || isSubmitting || isUploading}
          onChange={(event) => {
            void onFileSelected(event.target.files);
            event.target.value = "";
          }}
        />
      </div>
    </Form>
  );
};
